import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { trackScrollDepth } from '../util/analytics';

export const ScrollTracker = () => {
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const sentinel50Ref = useRef<HTMLDivElement>(null);
  const sentinel90Ref = useRef<HTMLDivElement>(null);
  const tracked50 = useRef(false);
  const tracked90 = useRef(false);
  const [docHeight, setDocHeight] = useState(0);

  useEffect(() => {
    tracked50.current = false;
    tracked90.current = false;
  }, [location.pathname]);

  useEffect(() => {
    const updateHeight = () => {
      setDocHeight(document.documentElement.scrollHeight);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(document.body);

    return () => resizeObserver.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const depth = Number(entry.target.getAttribute('data-depth'));

          if (depth === 50 && !tracked50.current) {
            tracked50.current = true;
            trackScrollDepth(50);
          }

          if (depth === 90 && !tracked90.current) {
            tracked90.current = true;
            trackScrollDepth(90);
          }
        });
      },
      { threshold: 0 }
    );

    if (sentinel50Ref.current) observer.observe(sentinel50Ref.current);
    if (sentinel90Ref.current) observer.observe(sentinel90Ref.current);

    return () => observer.disconnect();
  }, [location.pathname, docHeight]);

  if (docHeight === 0) return null;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '1px',
        height: docHeight,
        pointerEvents: 'none',
        zIndex: -1
      }}>
      <div ref={sentinel50Ref} data-depth={50} style={{ position: 'absolute', top: '50%', width: '1px', height: '1px' }} />
      <div ref={sentinel90Ref} data-depth={90} style={{ position: 'absolute', top: '90%', width: '1px', height: '1px' }} />
    </div>
  );
};

export default ScrollTracker;
